import React, { useEffect, useState, useMemo } from "react";
import { ThemeProvider, createTheme, GlobalStyles, CircularProgress, Box } from "@mui/material";
import PropTypes from "prop-types";
import { fetchGlobalConfigColors } from "../utils/contentfulRequests";

// Opcional: puedes ajustar estos valores base según tu diseño
const baseThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: "Roboto, Arial, sans-serif",
    body1: {
      fontSize: "16px",
      lineHeight: "1.6",
    },
    body2: {
      fontSize: "14px",
      lineHeight: "1.5",
    },
  },
  shape: {
    borderRadius: "4px",
  },
  dimensions: {
    maxContentWidth: "1200px",
    maxCardWidth: "400px",
    headerHeight: "64px",
  },
  palette: {
    primary: {
      main: '#0960ae', // Azul Oscuro 
    },
    secondary: {
      main: '#FFFFFF', // Blanco
    },
    blueLight: {
      main: '#72CCF4', // Azul Claro
    },
    white: {
        main:'#FFFF' // Blanco
    },
    grayLight: {
      main: '#F7F8FA', // Gris Claro 
    },
    backgroundNews: {
      main: '#003365', // Fondo oscuro para noticias
    },
    highlight: {
      main: '#F86B16', // Naranja para botones o acentos
    },
    dividerMobile: {
      main: '#D1D5DB', // Gris Claro 2
    },
    background: {
      default: '#FFFFFF', // Gris Claro
      component: '#F5F7F8', // Gris
    },
    counters: {
      main:'#003366',
      secondary:'#72CCF4',
    }
  },
};

const globalStyles = (
  <GlobalStyles
    styles={{
      "p, h1, h2, h3, h4, h5, h6, ul, ol, li, table, td, th, tr": {
        margin: 0,
        padding: 0,
      },
      "ul, ol": {
        listStyleType: "disc",
        margin: "0 0 16px 24px",
      },
      "h1, h2, h3, h4, h5, h6": {
        margin: "0",
      },
      p: {
        marginBottom: "4px",
      },
      table: {
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "24px",
        backgroundColor: "#FFF",
        border: "1px solid #E0E0E0",
      },
      "td, th": {
        padding: "8px",
        border: "1px solid #E0E0E0",
        textAlign: "left",
      },
      th: {
        backgroundColor: "#0960ae",
        color: "#FFFFFF",
        fontWeight: "bold",
      },
      tr: {
        "&:nth-of-type(odd)": {
          backgroundColor: "#F9F9F9",
        },
      },
      img: {
        maxWidth: "100%",
        height: "auto",
      },
      body: {
        margin: 0,
        overflow: "auto",
      },
      "::-webkit-scrollbar": {
        display: "none",
      },
      msOverflowStyle: "none",
      scrollbarWidth: "none",
      // --- Fixes para slick arrows (evita scroll horizontal extra) ---
      ".slick-next": {
        right: "8px !important",
        zIndex: 2,
        display: "none !important",
      },
      ".slick-prev": {
        left: "8px !important",
        zIndex: 2,
        display: "none !important",
      },
      
    }}
  />
);

export function DynamicThemeProvider({ children }) {
  const [colors, setColors] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGlobalConfigColors()
      .then((res) => {
        setColors(res);
      })
      .catch(() => {
        // no-op
      })
      .finally(() => setLoading(false));
  }, []);
  
  const theme = useMemo(() => {
    if (!colors) return createTheme(baseThemeOptions);

    return createTheme({
      ...baseThemeOptions,
      palette: {
        ...baseThemeOptions.palette,
        primary: {
          main: colors.primary,
          contrastText: "#fff",
        },
        secondary: {
          main: colors.secondary,
        },
        highlight: {
          main: colors.tertiary,
        },
        grayLight: {
            main: colors.fondosSecundarios
          },
      },
    });
  }, [colors]);

  return (
    <ThemeProvider theme={theme}>
      {globalStyles}
      {loading ? (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : (
        children
      )}
    </ThemeProvider>
  );
}

DynamicThemeProvider.propTypes = {
  children: PropTypes.node,
};
